import { AttributeTypeEnum } from "@sinch/core/entities/serverEnums";
import { FormatJsIntlProvider } from "@sinch/core/providers/FormatJsIntlProvider";
import { mapToOptions } from "@sinch/utils/option";
import { last } from "ramda";
import { ensureArray, isNotNil } from "ramda-adjunct";
import React, { Ref, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { AutocompleteInput } from "../../form/AutocompleteInput/AutocompleteInput";
import { AutocompleteOptions } from "../../form/AutocompleteInput/types";
import { DateInput } from "../../form/DateInput/DateInput";
import { useMuiController } from "../../form/MuiController/MuiController";
import { TextInput } from "../../form/TextInput/TextInput";
import { DropZoneImageUpload } from "../../form/UploadInput/DropZoneImageUpload/DropZoneImageUpload";
import { UploadChipPreviews } from "../../form/UploadInput/UploadChipPreview/UploadChipPreviews";
import { CountrySelect } from "../CountrySelect/CountrySelect";
import { BaseAttribute } from "./types";
import { SetOptional } from "type-fest";

type CustomAttributeInputProps<
  AttributeType extends Omit<BaseAttribute, "name"> & Partial<Pick<BaseAttribute, "name">>,
> = Omit<
  SetOptional<ReturnType<typeof useMuiController>["field"], "name" | "onBlur" | "error" | "errorMessage" | "inputRef">,
  "inputRef"
> & {
  attribute: AttributeType;
  inputRef?: Ref<HTMLInputElement>;
  disablePortal?: boolean;
  previewSize?: "small" | "medium" | "large";
  placeholder?: string;
};

export const CustomAttributeInputBase = <AttributeType extends BaseAttribute>({
  attribute,
  inputRef,
  disablePortal,
  previewSize,
  ...props
}: CustomAttributeInputProps<AttributeType>) => {
  const { t } = useTranslation();

  const options = useMemo(() => mapToOptions("id", "name")(attribute.options || []), [attribute.options]);

  const booleanOptions = useMemo<AutocompleteOptions[]>(
    () => [
      // TODO: Figure out how to use true and false as values
      { value: "", label: t("Attribute.notSet") },
      { value: 1, label: t("yes") },
      { value: 0, label: t("no") },
    ],
    [t]
  );

  switch (attribute.dataType) {
    case AttributeTypeEnum.Bool:
      return (
        <AutocompleteInput
          {...props}
          disablePortal={disablePortal}
          inputRef={inputRef}
          label={attribute.name}
          onChange={(val) => {
            if (val === 1) {
              props.onChange(true);
            } else if (val === 0) {
              props.onChange(false);
            } else {
              props.onChange(val);
            }
          }}
          options={booleanOptions}
          placeholder={props.placeholder ?? t("enterPlaceholder", {
            inputName: attribute.name?.toLowerCase(),
          })}
          value={props.value === true ? 1 : props.value === false ? 0 : ""}
        />
      );
    case AttributeTypeEnum.Country:
      return (
        <CountrySelect
          {...props}
          disablePortal={disablePortal}
          inputRef={inputRef}
          label={attribute.name}
          placeholder={props.placeholder ?? t("enterPlaceholder", {
            inputName: attribute.name?.toLowerCase(),
          })}
        />
      );
    case AttributeTypeEnum.Choice:
      return (
        <AutocompleteInput
          {...props}
          disablePortal={disablePortal}
          inputRef={inputRef}
          label={attribute.name}
          options={options}
          placeholder={props.placeholder ?? t("enterPlaceholder", {
            inputName: attribute.name?.toLowerCase(),
          })}
          value={isNotNil(props.value) ? String(props.value) : props.value}
        />
      );
    case AttributeTypeEnum.Text:
      return (
        <TextInput
          {...props}
          inputRef={inputRef}
          label={attribute.name}
          placeholder={props.placeholder ?? t("enterPlaceholder", {
            inputName: attribute.name?.toLowerCase(),
          })}
        />
      );
    case AttributeTypeEnum.Date:
      return (
        // Set local timezone for providers
        <FormatJsIntlProvider>
          <DateInput {...props} disablePortal={disablePortal} inputRef={inputRef} label={attribute.name} />
        </FormatJsIntlProvider>
      );
    case AttributeTypeEnum.File:
      return (
        <UploadChipPreviews
          {...props}
          endpoint="/admin/attributes/uploadFile"
          label={attribute.name}
          onChange={(files) => {
            props.onChange(last(files) ?? []);
          }}
          title={t("Action.uploadFile")}
          value={ensureArray(props.value ?? [])}
        />
      );
    case AttributeTypeEnum.Gallery:
      return (
        <DropZoneImageUpload
          {...props}
          endpoint="/admin/attributes/uploadFile/1"
          label={attribute.name}
          multiple
          slotProps={{ previewItem: { previewSize: previewSize ?? "medium" } }}
        />
      );
    case AttributeTypeEnum.Image:
      return (
        <DropZoneImageUpload
          {...props}
          endpoint="/admin/attributes/uploadFile/1"
          label={attribute.name}
          onChange={(files) => props.onChange(last(files) ?? [])}
          value={ensureArray(props.value ?? [])}
        />
      );
    case AttributeTypeEnum.Int:
      return (
        <TextInput
          {...props}
          allowEmpty
          inputRef={inputRef}
          label={attribute.name}
          placeholder={props.placeholder ?? t("enterPlaceholder", {
            inputName: attribute.name?.toLowerCase(),
          })}
          type="number"
        />
      );
    case AttributeTypeEnum.Multiplechoice:
      return (
        <AutocompleteInput
          {...props}
          disablePortal={disablePortal}
          inputRef={inputRef}
          label={attribute.name}
          multiple
          options={options}
          placeholder={props.placeholder ?? t("enterPlaceholder", {
            inputName: attribute.name?.toLowerCase(),
          })}
          value={(props.value ?? []).map((val: any) => (isNotNil(val) ? String(val) : val))}
        />
      );
    case AttributeTypeEnum.Range:
      return (
        <AutocompleteInput
          {...props}
          disablePortal={disablePortal}
          inputRef={inputRef}
          label={attribute.name}
          options={options}
          placeholder={props.placeholder ?? t("enterPlaceholder", {
            inputName: attribute.name?.toLowerCase(),
          })}
          value={isNotNil(props.value) ? String(props.value) : props.value}
        />
      );
    case AttributeTypeEnum.Textarea:
      return (
        <TextInput
          {...props}
          inputRef={inputRef}
          label={attribute.name}
          maxRows={15}
          minRows={5}
          multiline
          placeholder={props.placeholder ?? t("enterPlaceholder", {
            inputName: attribute.name?.toLowerCase(),
          })}
        />
      );
    default:
      return null;
  }
};
